import type { ReactNode } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { SectionCta } from "@/components/site/SectionCta";
import { ProcedureFigure } from "@/components/site/ProcedureFigure";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { ES_PAGE_MEDIA, procedureImageObjects } from "@/lib/procedure-media";

const FAQS: { q: string; a: string; node?: ReactNode }[] = [
  {
    q: "¿Es mejor el bypass gástrico o la manga gástrica?",
    a: "Ninguno de los dos es mejor para todos; son herramientas distintas. El bypass produce una pérdida de peso a largo plazo un poco mayor, mejor remisión de la diabetes y casi siempre mejora el reflujo. La manga es una operación más corta y sencilla que no toca el intestino. La opción correcta depende de su historia de salud, sus medicamentos y si tiene reflujo.",
  },
  {
    q: "¿Cuánto peso voy a perder con el bypass frente a la manga?",
    a: "En promedio, los pacientes de bypass pierden entre el 70 y el 79 por ciento del exceso de peso corporal, y los de manga entre el 67 y el 79 por ciento. Durante los primeros años, ambas operaciones rinden de forma similar; la diferencia se abre después de cuatro o cinco años.",
  },
  {
    q: "¿Cuál operación es mejor para el reflujo?",
    a: "El bypass gástrico. Es la operación anti-reflujo más confiable de la cirugía bariátrica. La manga puede empeorar un reflujo ya existente, y entre el 10 y el 20 por ciento de los pacientes sin reflujo lo desarrollan después de la manga. Reparar una hernia hiatal en el momento de la cirugía de manga hace menos probable el reflujo después de la operación, pero aún puede ocurrir.",
    node: (
      <>
        El bypass gástrico. Es la operación anti-reflujo más confiable de la cirugía bariátrica. La manga puede empeorar un reflujo ya existente, y entre el 10 y el 20 por ciento de los pacientes sin reflujo lo desarrollan después de la manga.{" "}
        <Link to="/es/reparacion-de-hernia-hiatal" className="text-brand-deep underline hover:no-underline">Reparar una hernia hiatal</Link> en el momento de la cirugía de manga hace menos probable el reflujo después de la operación, pero aún puede ocurrir.
      </>
    ),
  },
  {
    q: "¿Cuál operación es mejor para la diabetes tipo 2?",
    a: "Las dos mejoran el azúcar en la sangre de forma notable, muchas veces antes de que haya una pérdida de peso importante. El bypass tiene una ligera ventaja: en un estudio controlado en Noruega, la mitad de los pacientes de bypass tenían una HbA1c menor a 6.0 por ciento cinco años después, frente a una quinta parte de los pacientes de manga.",
  },
  {
    q: "¿Se puede revertir la manga o el bypass?",
    a: "La manga extirpa la mayor parte del estómago y es permanente. El bypass técnicamente se puede reconstruir, pero rara vez se hace. La manga sí se puede convertir en bypass más adelante si el reflujo o la recuperación de peso se vuelven un problema.",
  },
  {
    q: "¿Qué tan seguras son ambas operaciones?",
    a: "Muy seguras. En las grandes bases de datos nacionales, la mortalidad a 30 días promedia entre uno y dos pacientes de cada mil, similar a la cirugía de vesícula y menor que la de reemplazo de cadera. Cada operación tiene sus propios riesgos característicos: fuga de la línea de grapas y estrechez en la manga; úlcera marginal y hernia interna en el bypass.",
  },
];

export const Route = createFileRoute("/es/bypass-o-manga-gastrica")({
  head: () => {
    const base = esHead("bypassVsSleeve");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("bypassVsSleeve"),
              ...procedureImageObjects(ES_PAGE_MEDIA["/es/bypass-o-manga-gastrica"], "es", `${SITE}/es/bypass-o-manga-gastrica`),
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/bypass-o-manga-gastrica#faq`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${SITE}/es/bypass-o-manga-gastrica#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsBypassVsSleeve,
});

const ROWS: { label: string; sleeve: string; bypass: string }[] = [
  {
    label: "Qué sucede",
    sleeve: "Se extirpa cerca del 75 al 80 por ciento del estómago, dejando un tubo angosto. El intestino no se toca.",
    bypass: "Se crea una pequeña bolsa que se conecta directamente al intestino delgado, dejando fuera de circuito el resto del estómago (90 a 95 por ciento) y el duodeno.",
  },
  { label: "% de exceso de peso perdido*", sleeve: "67 a 79%", bypass: "70 a 79%" },
  {
    label: "Diabetes",
    sleeve: "Mejora marcada; remisión en muchos pacientes",
    bypass: "Tasas de remisión algo mayores y más duraderas",
  },
  {
    label: "Reflujo (ERGE)",
    sleeve: "Puede empeorar el reflujo existente o provocar uno nuevo",
    bypass: "Casi siempre mejora el reflujo; la opción preferida si el reflujo es un problema",
  },
  { label: "Tiempo de cirugía", sleeve: "Cerca de 45 minutos, una línea de grapas", bypass: "Cerca de 1 hora, dos conexiones" },
  { label: "Vitaminas", sleeve: "Suplementos de por vida", bypass: "Suplementos de por vida" },
  {
    label: "Principales riesgos a largo plazo",
    sleeve: "Reflujo, dilatación de la manga, recuperación de peso",
    bypass: "Úlcera marginal, síndrome de vaciamiento rápido, deficiencia de nutrientes, hernia interna",
  },
];

function EsBypassVsSleeve() {
  return (
    <EsPageShell
      page="bypassVsSleeve"
      title="Bypass gástrico o manga gástrica: ¿cuál elegir?"
      intro="El bypass gástrico Roux-en-Y por laparoscopia y la manga gástrica por laparoscopia son las operaciones de pérdida de peso que más se realizan en Estados Unidos. Ninguna es «mejor» para todos: son herramientas distintas, y la opción correcta depende de su historia de salud, sus medicamentos y cómo quiere que sea su vida después."
    >
      <EsSection id="como-funcionan" heading="¿Cómo hacen bajar de peso el bypass y la manga?">
        <p>
          En la{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>
          , el cirujano usa una engrapadora quirúrgica para extirpar entre el 75 y el 80 por ciento
          del estómago. Lo que queda es un estómago sellado, en forma de tubo, con un tamaño y una
          forma cercanos a los de un plátano. No se reconecta nada; el intestino no se toca. Come
          menos porque el nuevo estómago cabe menos, y siente menos hambre porque la parte que se
          quitó era la que producía la mayor parte de la grelina, una hormona del hambre.
        </p>
        <p>
          En el{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>
          , el cirujano usa la misma engrapadora para crear una pequeña bolsa de estómago, del
          tamaño aproximado de un huevo, y la conecta directamente al intestino delgado. Después de
          la cirugía, la comida «rodea» la parte más grande del estómago, además de un tramo corto
          de intestino. Esa bolsa pequeña limita cuánto come mediante señales nerviosas al cerebro
          y también envía una señal hormonal al cerebro y al páncreas, lo que produce sensación de
          saciedad y mejor control del azúcar en la sangre.
        </p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6 items-start">
          <ProcedureFigure
            media="sleeveDiagram"
            lang="es"
            className="flex flex-col items-center"
            imgClassName="w-2/3 sm:w-full max-w-[14rem] h-auto rounded-lg border border-border"
            caption={{ es: "Manga gástrica: un tubo gástrico angosto en forma de plátano" }}
          />
          <ProcedureFigure
            media="bypassDiagram"
            lang="es"
            className="flex flex-col items-center"
            imgClassName="w-2/3 sm:w-full max-w-[14rem] h-auto rounded-lg border border-border"
          />
        </div>
      </EsSection>

      <EsSection id="comparacion" heading="Comparación lado a lado" maxWidth="7xl">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <caption className="sr-only">
              Comparación entre la manga gástrica y el bypass gástrico según anatomía, pérdida de
              peso, diabetes, reflujo, tiempo de cirugía, vitaminas y riesgos a largo plazo
            </caption>
            <thead>
              <tr className="bg-brand-soft/50">
                <th scope="col" className="border border-border p-3 font-serif" />
                <th scope="col" className="border border-border p-3 font-serif">
                  Manga gástrica
                </th>
                <th scope="col" className="border border-border p-3 font-serif">
                  Bypass gástrico
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => {
                const hasAsterisk = r.label.endsWith("*");
                const labelText = hasAsterisk ? r.label.slice(0, -1) : r.label;
                return (
                  <tr key={r.label} className="align-top">
                    <th scope="row" className="border border-border p-3 font-semibold text-foreground">
                      {labelText}
                      {hasAsterisk && <sup className="text-xs">*</sup>}
                    </th>
                    <td className="border border-border p-3 text-foreground/80">{r.sleeve}</td>
                    <td className="border border-border p-3 text-foreground/80">{r.bypass}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          * Los rangos reflejan los datos de seguimiento de pacientes del Dr. Fitzer (MBSAQIP).
        </p>
      </EsSection>

      <EsSection id="largo-plazo" heading="¿Cómo se mantiene la pérdida de peso a largo plazo?">
        <p>
          Durante los primeros años, las dos operaciones suelen rendir de forma similar. La brecha
          se abre después de cuatro o cinco años. En estudios aleatorizados que siguieron a los
          pacientes durante una década completa, los pacientes de bypass tuvieron una pérdida de
          peso un poco mayor que los de manga —por ejemplo, 65 por ciento frente a 61 por ciento del
          exceso de IMC perdido en un estudio suizo— y los pacientes de manga tuvieron más
          probabilidad de necesitar una segunda operación por pérdida de peso insuficiente. Son
          promedios: la variación entre personas con la misma operación es mucho mayor que la
          diferencia promedio entre las dos operaciones.
        </p>
        <p>
          Así les ha ido a los pacientes del Dr. Fitzer. La primera gráfica muestra la pérdida del
          exceso de peso a diez años después del bypass gástrico; la segunda muestra la misma
          medida después de la manga gástrica.
        </p>
        <ProcedureFigure
          media="bypassGraph"
          lang="es"
          className="mt-4"
          sizes="(min-width: 1024px) 896px, calc(100vw - 3rem)"
          imgClassName="w-full h-auto rounded-lg border border-border"
        />
        <ProcedureFigure
          media="sleeveGraph"
          lang="es"
          className="mt-6"
          sizes="(min-width: 1024px) 896px, calc(100vw - 3rem)"
          imgClassName="block w-full h-auto rounded-lg"
        />
      </EsSection>

      <EsSection id="diabetes" heading="Diabetes tipo 2: el bypass tiene una ligera ventaja">
        <p>
          Las dos operaciones mejoran el azúcar en la sangre de forma notable, muchas veces de
          inmediato, antes de que haya una pérdida de peso importante. El bypass suele tener una
          ligera ventaja en el control de la diabetes. En un estudio noruego cuidadosamente
          controlado, la mitad de los pacientes de bypass tenían una HbA1c menor al 6.0 por ciento
          cinco años después, frente a una quinta parte de los pacientes de manga. Los cirujanos
          suelen inclinarse por el bypass en pacientes con diabetes de muchos años, que usan
          insulina o cuyo azúcar ha sido difícil de controlar.
        </p>
      </EsSection>

      <EsSection id="reflujo" heading="Reflujo: una diferencia clara entre las dos operaciones">
        <p>
          Aquí es donde las dos operaciones realmente difieren, y suele ser un factor que los
          pacientes subestiman: la manga tiene cierto problema de reflujo. Si ya tiene reflujo, la
          manga suele empeorarlo. En personas sin reflujo, la manga provoca uno lo bastante fuerte
          como para necesitar una pastilla diaria en el 10 a 20 por ciento de los casos. Alrededor
          del 5 por ciento de los pacientes de manga eventualmente buscan convertirla en bypass
          específicamente para aliviar la acidez.
        </p>
        <p>
          El bypass gástrico, en cambio, es la operación anti-reflujo más confiable de la cirugía
          bariátrica; que la acidez mejore o desaparezca es lo habitual. Si ya toma un inhibidor de
          bomba de protones a diario,{" "}
          <Link
            to="/es/reparacion-de-hernia-hiatal"
            className="font-medium text-brand-logo hover:underline"
          >
            tiene una hernia hiatal
          </Link>{" "}
          o le diagnosticaron esófago de Barrett, es probable que su cirujano lo oriente hacia el
          bypass.
        </p>
      </EsSection>

      <EsSection id="seguridad" heading="¿Qué tan seguras son estas dos operaciones?">
        <p>
          Ambas son muy seguras. En las grandes bases de datos nacionales, la mortalidad a 30 días
          promedia entre uno y dos pacientes de cada mil, similar a la cirugía de vesícula y menor
          que la de reemplazo de cadera. Esas mismas bases de datos ubican las complicaciones
          serias en alrededor del uno por ciento para la manga, y algo más alto para el bypass,
          reflejando su mayor complejidad. Cada una tiene sus riesgos característicos: fuga de la
          línea de grapas y estrechez en la manga; úlcera marginal y hernia interna en el bypass,
          esta última capaz de aparecer años después.
        </p>
      </EsSection>

      <EsSection id="vida-diaria" heading="Convivir con la operación elegida">
        <p>
          La cirugía es una herramienta, no una meta final. Ambas operaciones exigen hábitos de por
          vida, pero con matices distintos: el bypass es más exigente con las vitaminas (B12,
          hierro, calcio, vitamina D) y con los análisis de sangre regulares; después del bypass,
          los antiinflamatorios de venta libre como el ibuprofeno generalmente quedan restringidos
          por el riesgo de úlcera. Los pacientes de bypass pueden experimentar el síndrome de
          vaciamiento rápido —cólicos, sofocos y diarrea después de comidas azucaradas o
          grasosas—, algo que rara vez ocurre con la manga. Ambas operaciones aumentan la velocidad
          con que sube el alcohol en la sangre.
        </p>
        <p>
          La recuperación leve de peso después de los dos años es normal y esperada; una
          recuperación importante afecta a una minoría. Asistir a las citas de seguimiento es uno
          de los predictores más fuertes de mantener el peso, y los{" "}
          <Link
            to="/es/medicamentos-para-bajar-de-peso"
            className="font-medium text-brand-logo hover:underline"
          >
            medicamentos modernos para bajar de peso
          </Link>{" "}
          hoy le dan al médico una opción adicional si hay recuperación de peso.
        </p>
      </EsSection>

      <EsSection id="reversibilidad" heading="¿Cuál operación se puede revertir?">
        <p>
          La manga implica extirpar la mayor parte del estómago, un órgano que no se puede
          «guardar para después»; es permanente. El bypass técnicamente se puede reconstruir o
          «revertir», pero rara vez se hace. La asimetría útil es la contraria: una manga se puede
          convertir en bypass más adelante si el reflujo o la recuperación de peso se vuelven un
          problema, un camino bien establecido que explicamos en nuestra página de{" "}
          <Link
            to="/es/cirugia-de-revision"
            className="font-medium text-brand-logo hover:underline"
          >
            cirugía de revisión
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="norte-de-virginia" heading="Cómo decidir entre bypass y manga en el norte de Virginia">
        <p>
          Ambas operaciones se realizan en INOVA Fair Oaks Hospital y ambas suelen implicar una noche en el hospital, así que el lugar y la logística de la recuperación son los mismos en cualquier caso. La decisión depende del reflujo, la diabetes y sus metas, que el Dr. Fitzer revisa con usted en nuestro consultorio de Herndon o por telesalud.
        </p>
        <p>
          Conozca más sobre la <Link to="/es/manga-gastrica" className="text-brand-deep underline hover:no-underline">manga gástrica en el Norte de Virginia</Link> y el <Link to="/es/bypass-gastrico" className="text-brand-deep underline hover:no-underline">bypass gástrico en el Norte de Virginia</Link>.
        </p>
        <p>
          ¿Preguntas sobre cuál operación le conviene? Llame a nuestro consultorio de Herndon al{" "}
          <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
            703-709-9771
          </a>
          .
        </p>
        <SectionCta to="/es/contacto" label="cta_bypass_vs_sleeve_location_es">Solicite una Consulta</SectionCta>
      </EsSection>

      <EsSection id="preguntas" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.node ?? f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>

      <EsSection id="hable-con-nosotros" heading="Conversemos sobre su caso">
        <p>
          ¿No va a usar el seguro? Puede ver{" "}
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            hash="costo-manga-gastrica"
            className="font-medium text-brand-logo hover:underline"
          >
            cuánto cuesta la manga gástrica
          </Link>{" "}
          y el{" "}
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            hash="costo-bypass-gastrico"
            className="font-medium text-brand-logo hover:underline"
          >
            costo del bypass gástrico
          </Link>{" "}
          con pago propio: un estimado de $19,200 y $23,200, respectivamente, incluyendo el
          honorario del cirujano, un día de hospital en INOVA Fair Oaks Hospital, un estimado de
          anestesia y un año de seguimiento de rutina con el Dr. Fitzer.
        </p>
        <p>

          Pregúntele a su cirujano con qué frecuencia realiza cada operación y cómo son sus propias
          tasas de complicaciones y de revisión. La decisión entre el bypass y la manga se toma
          mejor en consulta, no desde un sitio web. Escríbanos en{" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            contacto
          </Link>{" "}
          para agendar una cita.
        </p>
      </EsSection>
    </EsPageShell>
  );
}
